import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardCard, StatsGrid, StatItem, DataList, DataListItem } from '../../../shared/ui';
import { paymentsV2Api, PaymentV2, PaymentV2Filters } from '../../../api/paymentsV2Api';
import { paymentAllocationV2Api, PaymentAllocationDetailsV2, PaymentAllocationEntryV2 } from '../../../api/paymentAllocationV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import ManagedPaymentsFilter from '../components/ManagedPaymentsFilter';
import './ManagedPaymentsCard.css';

const ManagedPaymentsCard: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [payments, setPayments] = useState<PaymentV2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<PaymentV2Filters>({});
  const { user } = useAuth();
  const [expandedPaymentId, setExpandedPaymentId] = useState<number | null>(null);
  const [allocationDetails, setAllocationDetails] = useState<Record<number, PaymentAllocationDetailsV2>>({});
  const [allocationErrors, setAllocationErrors] = useState<Record<number, string>>({});
  const [detailsLoadingId, setDetailsLoadingId] = useState<number | null>(null);

  const loadPayments = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // Если период не выбран, сервер вернет последние 30 платежей по умолчанию
      const paymentsData = await paymentsV2Api.getAccessiblePayments(filters);
      setPayments(paymentsData);
      setExpandedPaymentId(null);
      setAllocationDetails({});
      setAllocationErrors({});
      setDetailsLoadingId(null);
    } catch (error) {
      console.error('Ошибка при загрузке доступных платежей:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filters]);

  useEffect(() => {
    if (!isCollapsed) {
      loadPayments();
    }
  }, [isCollapsed, loadPayments]);

  const handleFiltersChange = (newFilters: PaymentV2Filters) => {
    setFilters(newFilters);
  };

  const currencyFormatter = useMemo(() => new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0
  }), []);

  const formatCurrency = useCallback((amount: number) => {
    return currencyFormatter.format(amount);
  }, [currencyFormatter]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  }, []);

  const renderAllocationBottomContent = useCallback((paymentId: number): React.ReactNode => {
    if (detailsLoadingId === paymentId) {
      return (
        <div className="payment-allocation-details payment-allocation-details--loading">
          <i className="ri-loader-4-line ri-spin"></i>
          <span>Загрузка распределений...</span>
        </div>
      );
    }

    if (allocationErrors[paymentId]) {
      return (
        <div className="payment-allocation-details payment-allocation-details--error">
          <i className="ri-error-warning-line"></i>
          <span>{allocationErrors[paymentId]}</span>
        </div>
      );
    }

    const details = allocationDetails[paymentId];

    if (!details) {
      return null;
    }

    const hasAllocations = details.activityAllocations.length > 0 || details.contractAllocations.length > 0;

    return (
      <div className="payment-allocation-details">
        <div className="payment-allocation-summary">
          <div className="payment-allocation-summary__item">
            <span className="payment-allocation-summary__label">Распределено</span>
            <span className="payment-allocation-summary__value">{formatCurrency(details.totalAllocatedAmount)}</span>
          </div>
          <div className="payment-allocation-summary__item">
            <span className="payment-allocation-summary__label">Не распределено</span>
            <span className={`payment-allocation-summary__value ${details.remainingAmount > 0 ? 'warning' : 'success'}`}>
              {formatCurrency(details.remainingAmount)}
            </span>
          </div>
        </div>

        {hasAllocations ? (
          <>
            {details.activityAllocations.length > 0 && (
              <div className="payment-allocation-section">
                <div className="payment-allocation-section__title">
                  <i className="ri-user-2-line"></i>
                  <span>Сессии</span>
                </div>
                <ul className="payment-allocation-list">
                  {details.activityAllocations.map((allocation: PaymentAllocationEntryV2) => {
                    const parts: string[] = [];
                    if (allocation.sessionSiteName) parts.push(allocation.sessionSiteName);
                    if (typeof allocation.sessionHours === 'number') parts.push(`${allocation.sessionHours.toFixed(2)} ч`);
                    if (allocation.paymentLineType) parts.push(allocation.paymentLineType);
                    const description = parts.length > 0 ? parts.join(' • ') : 'Смена';

                    return (
                      <li key={`session-${allocation.allocationId}`} className="payment-allocation-list__item">
                        <div className="payment-allocation-list__info">
                          <span className="payment-allocation-list__title">{description}</span>
                        </div>
                        <span className="payment-allocation-list__amount">
                          {formatCurrency(allocation.allocationAmount)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {details.contractAllocations.length > 0 && (
              <div className="payment-allocation-section">
                <div className="payment-allocation-section__title">
                  <i className="ri-briefcase-2-line"></i>
                  <span>Контракты</span>
                </div>
                <ul className="payment-allocation-list">
                  {details.contractAllocations.map((allocation) => {
                    const subtitleParts: string[] = [];
                    if (allocation.contractDescription) {
                      subtitleParts.push(allocation.contractDescription);
                    }
                    if (allocation.paymentLineDescription) {
                      subtitleParts.push(allocation.paymentLineDescription);
                    }

                    return (
                      <li key={`contract-${allocation.allocationId}`} className="payment-allocation-list__item">
                        <div className="payment-allocation-list__info">
                          <span className="payment-allocation-list__title">{allocation.contractTitle ?? 'Контракт'}</span>
                          {subtitleParts.length > 0 && (
                            <span className="payment-allocation-list__subtitle">{subtitleParts.join(' • ')}</span>
                          )}
                        </div>
                        <span className="payment-allocation-list__amount">
                          {formatCurrency(allocation.allocationAmount)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        ) : (
          <div className="payment-allocation-details--empty">
            <i className="ri-information-line"></i>
            <span>Для этого платежа еще нет распределений.</span>
          </div>
        )}
      </div>
    );
  }, [allocationDetails, allocationErrors, detailsLoadingId, formatCurrency]);

  const handlePaymentClick = useCallback((payment: PaymentV2) => {
    if (expandedPaymentId === payment.id) {
      setExpandedPaymentId(null);
      return;
    }

    setExpandedPaymentId(payment.id);

    if (allocationDetails[payment.id]) {
      return;
    }

    setDetailsLoadingId(payment.id);
    setAllocationErrors((prev) => {
      const { [payment.id]: _removed, ...rest } = prev;
      return rest;
    });

    if (!payment.siteId) {
      setAllocationErrors((prev) => ({
        ...prev,
        [payment.id]: 'Не удалось определить объект для платежа'
      }));
      setDetailsLoadingId((prev) => (prev === payment.id ? null : prev));
      return;
    }

    paymentAllocationV2Api
      .getPaymentAllocations(payment.id, payment.siteId)
      .then((details) => {
        setAllocationDetails((prev) => ({
          ...prev,
          [payment.id]: details
        }));
      })
      .catch((error) => {
        console.error('Ошибка при загрузке распределений платежа:', error);
        setAllocationErrors((prev) => ({
          ...prev,
          [payment.id]: 'Не удалось загрузить распределения платежа'
        }));
      })
      .finally(() => {
        setDetailsLoadingId((prev) => (prev === payment.id ? null : prev));
      });
  }, [allocationDetails, expandedPaymentId]);

  // Подготовка элементов для DataList
  const dataListItems: DataListItem[] = useMemo(() => {
    const items: DataListItem[] = [];
    
    // Группируем платежи по дате
    const grouped: { [key: string]: PaymentV2[] } = {};
    
    payments.forEach(payment => {
      const dateKey = formatDate(payment.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(payment);
    });
    
    const sortedGroups = Object.entries(grouped).sort((a, b) => 
      new Date(b[0].split('.').reverse().join('-')).getTime() - 
      new Date(a[0].split('.').reverse().join('-')).getTime()
    );

    sortedGroups.forEach(([date, datePayments]) => {
      const totalAmountForDate = datePayments.reduce((sum, p) => sum + p.amount, 0);
      items.push({
        id: `date-${date}`,
        title: `${date} · ${formatCurrency(totalAmountForDate)}`,
        isHeader: true,
        className: 'payment-date-header'
      });

      // Добавляем элементы платежей: имя слева, сумма справа (без дублирования)
      datePayments.forEach((payment) => {
        const isPaymentSigned = payment.signatureAdmin && payment.signatureUser;
        const isExpanded = expandedPaymentId === payment.id;
        items.push({
          id: payment.id.toString(),
          title: payment.user?.name ?? 'Неизвестный',
          icon: isPaymentSigned ? 'ri-checkbox-circle-line' : 'ri-time-line',
          rightContent: (
            <span className="managed-payment-item-amount">
              {formatCurrency(payment.amount)}
            </span>
          ),
          details: [],
          className: `${isPaymentSigned ? 'signed' : 'pending'} ${isExpanded ? 'payment-item-expanded' : ''}`.trim(),
          onClick: () => handlePaymentClick(payment),
          bottomContent: isExpanded ? renderAllocationBottomContent(payment.id) : undefined
        });
      });
    });

    return items;
  }, [payments, formatCurrency, formatDate, expandedPaymentId, handlePaymentClick, renderAllocationBottomContent]);

  const getTotalAmount = () => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getSignedPayments = () => {
    return payments.filter(p => p.signatureAdmin && p.signatureUser).length;
  };

  const getPendingPayments = () => {
    return payments.filter(p => !p.signatureAdmin || !p.signatureUser).length;
  };

  const statsData: StatItem[] = [
    {
      id: 'total',
      value: formatCurrency(getTotalAmount()),
      label: 'Всего',
      icon: 'ri-money-dollar-circle-line',
      color: 'muted'
    },
    {
      id: 'count',
      value: payments.length.toString(),
      label: 'Платежей',
      icon: 'ri-file-list-line',
      color: 'muted'
    },
    {
      id: 'signed',
      value: getSignedPayments().toString(),
      label: 'Подписано',
      icon: 'ri-checkbox-circle-line',
      color: 'success'
    },
    {
      id: 'pending',
      value: getPendingPayments().toString(),
      label: 'Ожидает',
      icon: 'ri-time-line',
      color: 'warning'
    }
  ];

  return (
    <DashboardCard
      id="managed-payments-card"
      title="Общие платежи сотрудникам"
      icon="ri-money-dollar-circle-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      className="managed-payments-card"
    >
      {isLoading ? (
        <div className="managed-payments-loading">
          <div className="loading-spinner">
            <i className="ri-loader-4-line"></i>
          </div>
          <span>Загрузка платежей...</span>
        </div>
      ) : (
        <>
          <StatsGrid items={statsData} />

          {/* Фильтры */}
          <ManagedPaymentsFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            loading={isLoading}
          />

          <DataList
            className="managed-payments-list"
            items={dataListItems}
            variant="detailed"
            showDividers={true}
            headerStyle="minimal"
            emptyState={{
              icon: 'ri-inbox-line',
              title: 'Нет доступных платежей',
              subtitle: 'Платежи отображаются только для объектов, где у вас есть права на создание смен и просмотр финансовых данных'
            }}
          />
        </>
      )}
    </DashboardCard>
  );
};

export default ManagedPaymentsCard;

